import type { CSSProperties, ReactNode } from 'react';
import { Icon } from '@garnet/ui';

/** Dark in-phone header with a back affordance and a live online/offline buffer indicator. */
export function PhoneHeader({
  title,
  onBack,
  offline,
  buffered,
  onToggleOffline,
}: {
  title: string;
  onBack?: (() => void) | undefined;
  offline: boolean;
  buffered: number;
  onToggleOffline: () => void;
}) {
  return (
    <div
      style={{
        background: 'var(--ink)',
        color: 'var(--paper)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          style={{ background: 'none', border: 0, color: 'var(--paper)', cursor: 'pointer', display: 'flex', padding: 0 }}
        >
          <Icon name="chevron" size={18} style={{ transform: 'rotate(180deg)' }} />
        </button>
      )}
      <div style={{ flex: 1, fontFamily: 'var(--display)', fontWeight: 700, fontSize: 15, letterSpacing: '-.01em' }}>
        {title}
      </div>
      <button
        type="button"
        onClick={onToggleOffline}
        aria-pressed={offline}
        aria-label={offline ? 'Offline — tap to go online' : 'Online — tap to simulate offline'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          fontFamily: 'var(--mono)',
          fontSize: 10,
          fontWeight: 600,
          padding: '3px 8px',
          borderRadius: 999,
          border: 0,
          cursor: 'pointer',
          background: offline ? 'rgba(182,134,44,.25)' : 'rgba(27,122,107,.25)',
          color: offline ? '#f0d79a' : '#a7e0d2',
        }}
      >
        <i
          style={{ width: 6, height: 6, borderRadius: '50%', background: offline ? '#e0b25a' : '#5fc7b3' }}
          aria-hidden="true"
        />
        {offline ? `offline · ${buffered} buffered` : 'online'}
      </button>
    </div>
  );
}

/** Bottom iOS-style tab bar for the four inspector screens. */
export function TabBar<T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: ReadonlyArray<{ key: T; label: string; icon: Parameters<typeof Icon>[0]['name'] }>;
  active: T;
  onChange: (key: T) => void;
}) {
  return (
    <nav
      aria-label="Inspector sections"
      style={{
        display: 'flex',
        borderTop: '1px solid var(--rule)',
        background: 'var(--panel)',
        paddingBottom: 18,
      }}
    >
      {tabs.map((t) => {
        const on = active === t.key;
        return (
          <button
            key={t.key}
            type="button"
            aria-current={on ? 'page' : undefined}
            onClick={() => onChange(t.key)}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              padding: '8px 0 4px',
              border: 0,
              background: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--mono)',
              fontSize: 9.5,
              letterSpacing: '.04em',
              textTransform: 'uppercase',
              color: on ? 'var(--ink)' : 'var(--muted)',
            }}
          >
            <Icon name={t.icon} size={18} />
            {t.label}
          </button>
        );
      })}
    </nav>
  );
}

/** A mono uppercase section eyebrow used inside cards. */
export function CardLabel({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        fontFamily: 'var(--mono)',
        fontSize: 10,
        letterSpacing: '.06em',
        textTransform: 'uppercase',
        color: 'var(--muted)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Garnet-styled compact button for the constrained phone surface. */
export function PhoneButton({
  children,
  primary,
  disabled,
  full,
  onClick,
}: {
  children: ReactNode;
  primary?: boolean;
  disabled?: boolean;
  full?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        fontFamily: 'var(--body)',
        fontSize: 14,
        fontWeight: 500,
        padding: '11px 16px',
        borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: `1px solid ${primary ? 'var(--ink)' : 'var(--rule)'}`,
        background: primary ? 'var(--ink)' : 'var(--panel)',
        color: primary ? 'var(--paper)' : 'var(--ink)',
        opacity: disabled ? 0.45 : 1,
        width: full ? '100%' : undefined,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
      }}
    >
      {children}
    </button>
  );
}
